<template>
    <v-container fluid class="pa-0">
        <banner :title="$t('Resources')"/>
        <v-container>        
            <v-row>
                <v-col 
                    cols="12" 
                    md="4" 
                    lg="3"
                >
                    <v-card
                        outlined
                        class="custom-radius"
                        :loading="loadingSubjects"
                        style="overflow: hidden;"
                    >
                        <v-card-text v-text="$t('Subjects')"></v-card-text>
                        <v-divider></v-divider>
                        <template v-if="subjects.length > 0">
                            <v-list 
                                dense
                                class="py-0"
                            >
                                <template v-for="(subject, index) in subjects">
                                    <v-list-item
                                        :class="{ 'active' : selectedSubject.index == index }"
                                        :key="subject.id"
                                        @click="selectSubjectEventHandler(index, subject)"
                                    >
                                        <v-list-item-title>
                                            {{ subject.teachingSubject.label }} ({{ subject.educationLevel.label }})
                                        </v-list-item-title>
                                    </v-list-item>
                                    <v-divider
                                        v-if="index + 1 < subjects.length"
                                        :key="index"
                                    ></v-divider>
                                </template>                                    
                            </v-list>
                        </template>
                        <template v-else>
                            <v-card-subtitle>{{ $t('NoRecords') }}</v-card-subtitle>
                        </template>
                    </v-card>
                </v-col>
                <v-col 
                    cols="12" 
                    md="8" 
                    lg="9"
                >
                    <v-card 
                        outlined
                        class="custom-radius"
                        :loading="loadingResources"
                    >
                        <v-card-text class="pb-0">
                            <div class="headline font-weight-medium mb-4" v-if="selectedSubject.teachingSubject">
                                <span v-text="selectedSubject.teachingSubject.label"></span> 
                                <span class="ml-2 subtitle-1">({{ selectedSubject.educationLevel.label }})</span>
                            </div>
                        </v-card-text>
                        <v-card flat color="grey lighten-4">
                            <v-divider></v-divider>
                            <v-row
                                align="center"
                                justify="space-between"
                                class="px-4"
                            >
                                <v-col cols="12" md="6">
                                    <span class="subtitle-1" v-text="$t('Resources')"></span>
                                    <v-btn 
                                        depressed
                                        small
                                        rounded
                                        class="ml-2"
                                        color="primary"
                                        @click="newResourceForm = !newResourceForm"
                                    ><v-icon left>mdi-plus</v-icon> {{ $t('AddNew') }}</v-btn>
                                </v-col>
                                <v-col cols="12" md="6" class="text-right">
                                    <v-chip
                                        label
                                        class="mx-1 my-1"
                                        :outlined="selectedTypeFilter.index != -1"
                                        color="secondary"
                                        @click="typeFilterEventHandler(-1, '')"
                                        v-text="$t('All')"
                                    ></v-chip>
                                    <v-chip
                                        label
                                        v-for="(type, index) in resourceTypes"
                                        :key="index"
                                        v-text="type.type"
                                        class="mx-1 my-1"
                                        :outlined="selectedTypeFilter.index != index"
                                        color="secondary"
                                        @click="typeFilterEventHandler(index, type.id)"
                                    ></v-chip>
                                </v-col>
                            </v-row>
                        </v-card>
                        <v-divider></v-divider>
                        <v-expand-transition>
                            <v-card 
                                v-show="newResourceForm"
                                flat 
                                color="grey lighten-4" 
                                class="pa-4 ma-4"
                            >
                                <v-row>
                                    <v-col cols="6">
                                        <v-select
                                            dense
                                            outlined
                                            rounded
                                            hide-details
                                            v-model="newResourceObject.type"
                                            :items="resourceTypes"
                                            item-text="type"
                                            item-value="id"
                                            :label="$t('Type')"
                                            @change="resourceTypeChangeEventHandler(newResourceObject)"
                                            :menu-props="{ offsetY: true }"
                                        ></v-select>
                                    </v-col>
                                    <v-col cols="6">
                                        <v-text-field
                                            dense
                                            outlined
                                            rounded
                                            hide-details
                                            v-model="newResourceObject.title"
                                            :label="$t('Title')"
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="12">
                                        <v-textarea
                                            dense
                                            outlined
                                            rounded
                                            hide-details
                                            v-model="newResourceObject.description"
                                            no-resize
                                            :label="$t('Description')"
                                            height="100"
                                        ></v-textarea>
                                    </v-col>
                                    <template v-if="newResourceObject.type == 0">
                                        <v-col cols="12">
                                            <v-file-input
                                                dense
                                                outlined
                                                rounded
                                                show-size 
                                                hide-details
                                                :label="$t('ArticleImageUpload')"
                                                prepend-icon=""
                                                prepend-inner-icon="mdi-camera"
                                                @change="fileUploadEventHandler($event, newResourceObject.image)"
                                            ></v-file-input>
                                        </v-col>
                                    </template>
                                    <template v-else-if="newResourceObject.type == 1">
                                        <v-col cols="6">
                                            <v-file-input
                                                dense
                                                outlined
                                                rounded
                                                show-size 
                                                hide-details
                                                :label="$t('DocumentUpload')"
                                                prepend-icon=""
                                                prepend-inner-icon="mdi-attachment"
                                                @change="fileUploadEventHandler($event, newResourceObject.document)"
                                            ></v-file-input>
                                        </v-col>
                                        <v-col cols="6">
                                            <v-text-field
                                                dense
                                                outlined
                                                rounded
                                                hide-details
                                                v-model="newResourceObject.documentTitle"
                                                :label="$t('DocumentTitle')"
                                            ></v-text-field>
                                        </v-col>
                                    </template>
                                    <template v-else-if="newResourceObject.type == 2">
                                        <v-col cols="12">
                                            <v-text-field
                                                dense
                                                outlined
                                                rounded
                                                hide-details
                                                v-model="newResourceObject.videoLink"
                                                :label="$t('VideoLink')"
                                            ></v-text-field>
                                        </v-col>
                                    </template>
                                    <v-col class="text-center">
                                        <v-btn
                                            rounded
                                            depressed
                                            color="primary"
                                            :loading="requestSubmitLoader"
                                            :disabled="requestSubmitLoader || !isCreateResourceFormValid"
                                            @click="createResource"
                                        >{{ $t('Save') }}</v-btn>
                                    </v-col>
                                </v-row>
                            </v-card>
                        </v-expand-transition>
                        <template v-if="resources.length > 0">
                            <template v-for="(resource, index) in resources">
                                <div class="pa-4" :key="`d${index}`">
                                    <div class="d-flex">
                                        <v-avatar :size="$vuetify.breakpoint.smAndDown ? 50 : 85" color="grey lighten-4">
                                            <template v-if="resource.type == 0">
                                                <template v-if="resource.image">
                                                    <v-img :src="`${baseImageUrl}${resource.image}`"></v-img>
                                                </template>
                                                <template v-else>
                                                    <v-icon>mdi-image</v-icon>
                                                </template>
                                            </template>
                                            <template v-else-if="resource.type == 1">
                                                <v-icon>mdi-file-document</v-icon>
                                            </template>
                                            <template v-else-if="resource.type == 2">
                                                <v-icon>mdi-play</v-icon>
                                            </template>
                                        </v-avatar>
                                        <div class="pl-4">
                                            <div class="title" v-text="resource.title"></div>
                                            <div class="body-2 grey--text" v-html="resource.description"></div>
                                            <div>
                                                <v-btn 
                                                    depressed
                                                    rounded
                                                    small
                                                    class="mt-2"
                                                    color="accent"
                                                    @click="previewResourceEventHandler(resource)"
                                                >{{ $t('PreviewDocuments') }}</v-btn>
                                                <v-btn 
                                                    depressed
                                                    rounded
                                                    small
                                                    color="primary"
                                                    class="mx-2 mt-2"
                                                    @click="updateResourceEventHandler(resource)"
                                                >{{ $t('Update') }}</v-btn>
                                                <v-btn 
                                                    depressed
                                                    rounded
                                                    small
                                                    class="mt-2"
                                                    color="secondary"
                                                    @click="deleteResourceEventHandler(resource.id)"
                                                >{{ $t('Delete') }}</v-btn>
                                            </div>
                                        </div>
                                    </div>                                    
                                </div>
                                <v-divider v-if="index + 1 < resources.length" :key="`v${index}`"></v-divider>
                            </template>
                        </template>
                        <template v-else>
                            <v-card-subtitle>{{ $t('NoRecords') }}</v-card-subtitle>
                        </template>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>
        <!-- Update resource dialog -->
        <v-dialog v-model="updateResourceDialog" max-width="600px">
            <v-card>
                <v-card-text class="py-4">
                    <v-row>
                        <v-col cols="6">
                            <v-select
                                dense
                                outlined
                                rounded
                                hide-details
                                v-model="updateResourceObject.resource.type"
                                :items="resourceTypes"
                                item-text="type"
                                item-value="id"
                                :label="$t('Type')"
                                @change="resourceTypeChangeEventHandler(updateResourceObject.resource)"
                            ></v-select>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                dense
                                outlined
                                rounded
                                hide-details
                                v-model="updateResourceObject.resource.title"
                                :label="$t('Title')"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12">
                            <v-textarea
                                dense
                                outlined
                                rounded
                                hide-details
                                v-model="updateResourceObject.resource.description"
                                no-resize
                                :label="$t('Description')"
                                height="200"
                            ></v-textarea>
                        </v-col>
                        <template v-if="updateResourceObject.resource.type == 0">
                            <v-col cols="12">
                                <v-file-input
                                    dense 
                                    outlined
                                    rounded
                                    hide-details
                                    show-size 
                                    :label="$t('ArticleImageUpload')"
                                    prepend-icon=""
                                    @change="fileUploadEventHandler($event, updateResourceObject.resource.image)"
                                ></v-file-input>
                            </v-col>
                        </template>
                        <template v-else-if="updateResourceObject.resource.type == 1">
                            <v-col cols="6">
                                <v-file-input
                                    dense 
                                    outlined
                                    rounded
                                    hide-details
                                    show-size 
                                    :label="$t('DocumentUpload')"
                                    prepend-icon=""
                                    @change="fileUploadEventHandler($event, updateResourceObject.resource.document)"
                                ></v-file-input>
                            </v-col>
                            <v-col cols="6">
                                <v-text-field
                                    dense
                                    outlined
                                    rounded
                                    hide-details
                                    v-model="updateResourceObject.resource.documentTitle"
                                    :label="$t('DocumentTitle')"
                                ></v-text-field>
                            </v-col>
                        </template>
                        <template v-else-if="updateResourceObject.resource.type == 2">
                            <v-col cols="12">
                                <v-text-field
                                    dense
                                    outlined
                                    rounded
                                    hide-details
                                    v-model="updateResourceObject.resource.videoLink"
                                    :label="$t('VideoLink')"
                                ></v-text-field>
                            </v-col>
                        </template>
                    </v-row>
                </v-card-text>
                <v-card-actions class="pa-4 pt-0">
                    <v-btn 
                        rounded
                        small 
                        depressed 
                        color="primary" 
                        :loading="requestSubmitLoader"
                        :disabled="requestSubmitLoader"
                        @click="updateResource"
                    >{{ $t('Update') }}</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn 
                        rounded
                        small 
                        depressed 
                        color="secondary"
                        @click="updateResourceDialog = false"
                        v-text="$t('Close')"
                    ></v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!-- Delete resource bottom sheet -->
        <v-bottom-sheet v-model="deleteResourceSheet">
            <v-sheet class="text-center py-4">
                <div class="py-3" v-text="$t('DeleteConfirmationNotification')"></div>
                <v-btn
                    text
                    color="red"
                    @click="deleteResource"
                    v-text="$t('Yes')"
                ></v-btn>
                <v-btn
                    text
                    color="primary"
                    @click="deleteResourceSheet = !deleteResourceSheet"
                    v-text="$t('No')"
                ></v-btn>
            </v-sheet>
        </v-bottom-sheet>
        <v-dialog
            v-model="previewOverlay"
            width="800"
        >
            <resource-details :resource="previewResource"/>
        </v-dialog>
    </v-container>
</template>

<script>
    import { mapGetters } from 'vuex'
    import Banner from '@/components/pageBanner'
    import Resource from '@/components/resourceCard'
    import ResourceDetails from '@/components/resourceCardDetails'
    
    export default {
        name: 'resources',
        data: () => ({
            resources: [],
            newResourceForm: false,
            newResourceObject: {
                type: -1,
                title: '',
                description: '',
                documentTitle: '',
                document: {},
                image: {},
                videoLink: '',
                teachingSubjectId: 0,
                educationLevelId: 0
            },            
            updateResourceObject: {
                id: 0,
                tutorId: 0,
                languageId: 0,
                resource: 
                {
                    type: 0,
                    title: '',
                    description: '',
                    documentTitle: '',
                    document: {},
                    image: {},
                    videoLink: '',
                    teachingSubjectId: 0,
                    educationLevelId: 0
                }
            },
            resourceTypes: [
                {
                    id: 0,
                    type: 'Article'
                },
                {
                    id: 1,
                    type: 'Document'
                },
                {
                    id: 2,
                    type: 'Video'
                }
            ],
            selectedTypeFilter: {
                index: -1,
                typeId: -1
            },
            subjects: [],
            selectedSubject: {},
            levels: [],
            updateResourceId: 0,
            updateResourceDialog: false,
            deleteResourceSheet: false,
            deleteResourceById: 0,
            requestSubmitLoader: false,
            previewResource: {},
            previewOverlay: false,
            loadingResources: false,
            loadingSubjects: true
        }),
        computed: {
            ...mapGetters(['user', 'baseImageUrl', 'preferedLanguage']),
            isCreateResourceFormValid() {
                return this.newResourceObject.type > -1 && this.newResourceObject.title !== ''
            }
        },
        components: {
            Banner,
            Resource,
            ResourceDetails
        },
        mounted() {
            this.fetchEducationLevels()
            this.fetchTeachingSubjects()
        },
        methods: {
            fetchEducationLevels() {
                this.axios.get('/EducationLevel/GetAll', {
                    params: {
                        languageId: this.preferedLanguage
                    }
                })
                .then(response => {
                    this.levels = response.data.list
                })
                .catch(error => {
                    const snackbar = {
                        show: true,
                        message: error.message
                    }
                    this.$EventBus.$emit('snackbar', snackbar)
                })
            },
            async fetchTeachingSubjects() {
                await this.axios.get('/TeachingSubject/GetAllByCoachId', {
                    params: {
                        coachId: this.user.id
                    }
                })
                .then(response => {
                    this.subjects = response.data.list
                    if(this.subjects.length > 0) {
                        this.selectedSubject = this.subjects[0]
                        this.selectedSubject.index = 0
                        this.newResourceObject.teachingSubjectId = this.subjects[0].teachingSubjectId
                        this.newResourceObject.educationLevelId = this.subjects[0].educationLevelId
                    }
                    this.filterResources()
                    this.loadingSubjects = false
                })
                .catch(error => {
                    const snackbar = {
                        show: true,
                        message: error.message
                    }
                    this.$EventBus.$emit('snackbar', snackbar)
                    this.loadingSubjects = false
                })
            },
            async filterResources() {
                this.loadingResources = true
                await this.axios.get('/Tutor/GetAllTutorResources', {
                    params: {
                        tutorId: this.user.id,
                        languageId: this.preferedLanguage,
                        teachingSubjectId: this.selectedSubject.teachingSubjectId,
                        educationLevelId: this.selectedSubject.educationLevelId,
                        type: this.selectedTypeFilter.typeId != -1 ? this.selectedTypeFilter.typeId : ''
                    }
                })
                .then(response => {
                    this.resources = response.data.list
                    this.loadingResources = false
                })
                .catch(error => {
                    const snackbar = {
                        show: true,
                        message: error.message
                    }
                    this.$EventBus.$emit('snackbar', snackbar)
                    this.loadingResources = false
                })
            },            
            fileUploadEventHandler(event, object) {
                if(typeof event == 'undefined') {
                    object = {}
                    return
                }
                const file = event
                object.fileName = file.name.split('.')[0]
                object.fileExtension = file.name.split('.')[1]

                var reader = new FileReader()
                reader.readAsDataURL(file)
                reader.onload = ()=> {
                    object.base64String = reader.result.replace(/^data:.+;base64,/, '')
                }
            },
            resourceTypeChangeEventHandler(object) {
                object.document = {}
                object.image = {}
            },
            resetFilterType() {
                this.typeFilterActiveIndex = -1
            },
            selectSubjectEventHandler(index, subject) {
                this.selectedSubject = subject
                this.selectedSubject.index = index
                this.newResourceObject.teachingSubjectId = subject.teachingSubjectId
                this.newResourceObject.educationLevelId = subject.educationLevelId
                this.filterResources(this.selectedSubject.teachingSubjectId, this.selectedSubject.educationLevelId, '')
            },
            typeFilterEventHandler(index, typeId) {
                this.selectedTypeFilter.index = index
                this.selectedTypeFilter.typeId = typeId
                this.filterResources()
            },
            createResource() {
                this.requestSubmitLoader = true
                const data = {
                    tutorId: this.user.id,
                    languageId: this.preferedLanguage,
                    resource: this.newResourceObject
                }
                this.axios.post('/Tutor/CreateTutorResources', data)
                .then(response => {
                    this.resources.push(response.data)
                    const snackbar = {
                        show: true,
                        message: 'Resource added successfully'
                    }
                    this.$EventBus.$emit('snackbar', snackbar)
                    this.requestSubmitLoader = false
                    this.newResourceForm = false
                    this.clearResourceObject()
                })
                .catch(error => {
                    const snackbar = {
                        show: true,
                        message: error.message
                    }
                    this.$EventBus.$emit('snackbar', snackbar)
                    this.requestSubmitLoader = false
                })
            },
            updateResourceEventHandler(resource) {
                this.updateResourceDialog = true
                this.updateResourceObject.id = resource.id
                this.updateResourceObject.tutorId = resource.coachId
                this.updateResourceObject.languageId = resource.languageId
                this.updateResourceObject.resource.type = resource.type
                this.updateResourceObject.resource.title = resource.title
                this.updateResourceObject.resource.description = resource.description
                this.updateResourceObject.resource.documentTitle = resource.documentTitle
                this.updateResourceObject.resource.educationLevelId = resource.educationLevelId
                this.updateResourceObject.resource.teachingSubjectId = resource.teachingSubjectId
                this.updateResourceObject.resource.videoLink = resource.videoLink
            },
            updateResource() {
                this.requestSubmitLoader = true
                const data = this.updateResourceObject                
                this.axios.post('/Tutor/UpdateTutorResources', data)
                .then(response => {
                    let index = this.resources.map(resource => {
                        return resource.id
                    }).indexOf(this.updateResourceObject.id)
                    this.resources[index] = response.data
                    this.updateResourceDialog = false
                    const snackbar = {
                        show: true,
                        message: 'Resource updated successfully'
                    }
                    this.$EventBus.$emit('snackbar', snackbar)
                    this.requestSubmitLoader = false
                })
                .catch(error => {
                    const snackbar = {
                        show: true,
                        message: error.message
                    }
                    this.$EventBus.$emit('snackbar', snackbar)
                    this.requestSubmitLoader = false
                })
            },
            deleteResourceEventHandler(id) {
                this.deleteResourceSheet = true
                this.deleteResourceById = id
            },
            deleteResource() {
                this.axios.delete('/Tutor/DeleteResource', {
                    params: {
                        resourceId: this.deleteResourceById
                    }
                })
                .then(response => {
                    let index = this.resources.map(resource => {
                        return resource.id
                    }).indexOf(this.deleteResourceById)
                    this.resources.splice(index, 1)
                    this.deleteResourceSheet = false
                    const snackbar = {
                        show: true,
                        message: 'Resource deleted successfully'
                    }
                    this.$EventBus.$emit('snackbar', snackbar)
                })
                .catch(error => {
                    const snackbar = {
                        show: true,
                        message: error.message
                    }
                    this.$EventBus.$emit('snackbar', snackbar)
                })
            },
            clearResourceObject() {
                this.newResourceObject.type = 5,
                this.newResourceObject.title = '',
                this.newResourceObject.description = '',
                this.newResourceObject.documentTitle = '',
                this.newResourceObject.document = {},
                this.newResourceObject.image = {},
                this.newResourceObject.videoLink = ''
            },
            previewResourceEventHandler(resource) {
                this.previewResource = resource
                this.previewOverlay = true
            }
        }
    }
</script>

<style lang="scss" scoped>
.resource-container{
    background-color: #f8f8f8;
}
.v-list-item.active{
    background-color: #eee;
}
</style>